import React, { useEffect, useState, useMemo, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import {
  Button,
  ButtonGroup,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Container,
  SelectDropdown,
  Grid,
  Input,
  Row,
} from '@uhg-uicl/ui-core/dist';
import { message } from 'antd';
import { ColumnType } from '@uhg-uicl/ui-core/dist/Organisms/Grid/types';
import { useMsal } from '@azure/msal-react';
import axios from 'axios';

// Point the worker at the static copy in /public so CRA can serve it without ejecting.
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

console.log("SET WORKER1:", pdfjsLib.GlobalWorkerOptions.workerSrc);

interface Props {
  isOpen?: boolean;
  setIsOpen?: (isOpen: boolean) => void;
  rrdInventoryItem: any;
  refreshData: any;
}

// Static objects outside the component
const docTypeHierarchy: { [key: string]: number } = {
  "Appeal Letter": 1,
  "Patient Letter": 1,
  "Patient Letter With Override Address": 1,
  "Payer Dispute Form": 1,
  "Payer Letter": 1,
  "Cover Letter": 1,
  "Ambulance Report": 2,
  "EOB": 2,
  "IZ Statement": 2,
  "Package": 1,
  "Other": 2,
  "Rate Sheet": 2,
  "W9 Form": 2,
  "Non Medical Record": 2,
  "Medical Record": 2,
  "Copy of Claim": 2,
};

const packageTypes: { [key: string]: string } = {
  "Medical Record": "PKG",
  "Non Medical Record": "NMR",
  "Cover Letter": "CVRLTR",
  "EOB": "PYRLTR",
  "Patient Letter With Override Address": "BRE",
  "Patient Letter": "PTLTR",
  "Appeal Letter": "APL",
  "Package": "PKG",
  "Rate Sheet": "APL",
  "Payer Letter": "PYRLTR",
};

const allowedExtensions = ['pdf'];

const columns: Set<ColumnType> = new Set([
  { dataName: 'documentType', text: 'Document Type', sortable: true, isSorted: 0 },
  { dataName: 'actualFileName', text: 'Actual File', sortable: true, isSorted: 0 },
  { dataName: 'fileName', text: 'File', sortable: true, isSorted: 0 },
]);

const docTypeOptions = [
  { label: 'Ambulance Report', value: 'Ambulance Report' },
  { label: 'Appeal Letter', value: 'Appeal Letter' },
  { label: 'Copy of Claim', value: 'Copy of Claim' },
  { label: 'EOB', value: 'EOB' },
  { label: 'IZ Statement', value: 'IZ Statement' },
  { label: 'Medical Record', value: 'Medical Record' },
  { label: 'Package', value: 'Package' },
  { label: 'Patient Letter', value: 'Patient Letter' },
  { label: 'Patient Letter With Override Address', value: 'Patient Letter With Override Address' },
  { label: 'Payer Dispute Form', value: 'Payer Dispute Form' },
  { label: 'Payer Letter', value: 'Payer Letter' },
  { label: 'Rate Sheet', value: 'Rate Sheet' },
  { label: 'W9 Form', value: 'W9 Form' },
  { label: 'Other', value: 'Other' },
];

const mailTypeOptions = [
  { label: 'Certified-CERT', value: 'CERT' },
  { label: 'Standard-STD', value: 'STD' },
];

function getPackageAbbreviation(key: string, requestType: string): string {
  if (packageTypes[key]) return packageTypes[key];
  if (
    key === 'Payer Dispute Form' ||
    key === 'Ambulance Report' ||
    key === 'Copy of Claim' ||
    key === 'W9 Form' ||
    key === 'Other' ||
    key === 'IZ Statement'
  ) {
    if (requestType === 'Payer') return 'PYRLTR';
    if (requestType === 'Patient') return 'PTLTR';
    return 'PYRLTR';
  }
  return 'PYRLTR';
}

// Validates a PDF file client-side before upload.
// Phase 1 (binary): checks file size, %PDF- magic bytes, and %%EOF end-of-file marker.
// Phase 2 (pdfjs): loads the PDF and processes each page's operator list so that all
//   embedded image streams (JPEG, PNG, JBIG2, CCITT, etc.) are decoded. A corrupt image
//   on any page causes pdfjs to throw, which we catch and surface to the user.
async function validatePDFFile(file: File): Promise<{ isValid: boolean; error?: string }> {
  if (file.size === 0) {
    return { isValid: false, error: 'The file is empty.' };
  }
  if (file.size < 100) {
    return { isValid: false, error: 'File is too small to be a valid PDF.' };
  }

  // --- Phase 1: fast binary header / EOF checks ---
  let buffer: ArrayBuffer;
  try {
    buffer = await new Promise<ArrayBuffer>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as ArrayBuffer);
      reader.onerror = () => reject(new Error('Failed to read the file. It may be corrupt or inaccessible.'));
      reader.readAsArrayBuffer(file);
    });
  } catch (err: any) {
    return { isValid: false, error: err?.message || 'Failed to read the file.' };
  }

  const bytes = new Uint8Array(buffer);

  // Validate %PDF- magic bytes
  if (
    bytes[0] !== 0x25 || bytes[1] !== 0x50 || bytes[2] !== 0x44 ||
    bytes[3] !== 0x46 || bytes[4] !== 0x2D
  ) {
    return { isValid: false, error: 'File is not a valid PDF (missing PDF header). It may be corrupt or incorrectly named.' };
  }

  // Validate %%EOF marker in the last 1 KB
  const tailStart = Math.max(0, bytes.length - 1024);
  const tail = bytes.slice(tailStart);
  let eofFound = false;
  for (let i = 0; i <= tail.length - 5; i++) {
    if (
      tail[i] === 0x25 && tail[i + 1] === 0x25 &&
      tail[i + 2] === 0x45 && tail[i + 3] === 0x4F && tail[i + 4] === 0x46
    ) {
      eofFound = true;
      break;
    }
  } console.log('eofFound:- ', eofFound);
  if (!eofFound) {
    return { isValid: false, error: 'PDF is incomplete or corrupt (missing EOF marker). The file may be truncated and could have missing pages.' };
  }

  // --- Phase 2: pdfjs page-by-page image stream validation ---
  // stopAtErrors: true promotes structural PDF errors to throws.
  // However, certain JPEG/image codec failures (e.g. "unexpected marker", missing EOI)
  // are always emitted as console.warn by pdfjs regardless of stopAtErrors.
  // We intercept console.warn per-page to catch those, while the try/catch handles thrown errors.

  // Patterns that indicate a corrupt or unreadable image in pdfjs warning messages
  const CORRUPT_IMAGE_PATTERNS = [
    'unable to decode image',
    'jpegerror',
    'unexpected marker',
    'end of the image data without finding an eoi',
    'unsupported predictor',
    'image decode',
  ];

  try {
    const loadingTask = pdfjsLib.getDocument({ data: buffer.slice(0), stopAtErrors: true });
    const pdf = await loadingTask.promise;

    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      // Capture console.warn for this page to catch JPEG/image codec warnings
      // that pdfjs emits instead of throwing.
      const capturedWarnings: string[] = [];
      const originalWarn = console.warn;
      console.warn = (...args: any[]) => {
        const msg = args.map((a) => (typeof a === 'string' ? a : String(a))).join(' ');
        capturedWarnings.push(msg);
        originalWarn.apply(console, args);
      };

      try {
        const page = await pdf.getPage(pageNum);
        await page.getOperatorList();
        page.cleanup();
      } catch (pageErr: any) {
        console.warn = originalWarn;
        await pdf.destroy();
        const msg = pageErr?.message || String(pageErr);
        return {
          isValid: false,
          error: `Page ${pageNum} contains a corrupt or unreadable image: ${msg}`,
        };
      } finally {
        console.warn = originalWarn;
      }

      // Check captured warnings for image decode problems
      const imageWarning = capturedWarnings.find((w) =>
        CORRUPT_IMAGE_PATTERNS.some((pattern) => w.toLowerCase().includes(pattern))
      );
      if (imageWarning) {
        await pdf.destroy();
        return {
          isValid: false,
          error: `Page ${pageNum} contains a corrupt or unreadable image: ${imageWarning.replace(/^Warning:\s*/i, '')}`,
        };
      }
    }

    await pdf.destroy();
  } catch (docErr: any) {
    const msg = docErr?.message || String(docErr);
    return { isValid: false, error: `Could not open PDF for validation: ${msg}` };
  }

  return { isValid: true };
}

const RRDDocumentUpload = React.memo(function RRDDocumentUpload({
  isOpen = false,
  setIsOpen = () => { },
  rrdInventoryItem,
  refreshData,
}: Props) {
  const [gridRecords, setGridRecords] = useState<any[]>([]);
  const { accounts } = useMsal();
  const userdetails = useMemo(
    () => JSON.parse(localStorage.getItem('userdetails') || '[]'),
    []
  );
  const userid = useMemo(
    () =>
      userdetails[0]?.portal_Id !== ''
        ? userdetails[0]?.portal_Id
        : accounts[0]?.username,
    [userdetails, accounts]
  );
  const [refreshList, setRefreshList] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [facilityCode, setFacilityCode] = useState('');
  interface UploadItem {
    id: number;
    selectedDocType: string;
    mailType: string;
    file: File | null;
    otherDocType: string;
    fileError?: string;
    hasValidatedFile?: boolean;
    isValidating?: boolean; // ✅ NEW
  }

  const [uploadItems, setUploadItems] = useState<UploadItem[]>([
    { id: 1, selectedDocType: '', mailType: '', file: null, otherDocType: '', hasValidatedFile: false },
  ]);

  const isAnyFileValidating = useMemo(
    () => uploadItems.some(item => item.isValidating),
    [uploadItems]
  );

  const uploadStatusMessage = useMemo(() => {
    if (uploading) return 'Uploading file(s)...';
    if (isAnyFileValidating) return 'Validating file(s)...';
    return '';
  }, [uploading, isAnyFileValidating]);

  useEffect(() => {
    setFacilityCode(
      rrdInventoryItem?.facilityCode
        ? rrdInventoryItem?.facilityCode
        : rrdInventoryItem?.senderCode
    );
  }, [isOpen, rrdInventoryItem]);

  const fetchForms = useCallback(async () => {
    if (!rrdInventoryItem?.inventoryMasterId) return;
    try {
      const baseurl = process.env.REACT_APP_RRD_OPTIMIZATION_BASE_URL as string;
      const url = `${baseurl}/InventoryFiles/ByInventoryMasterId/${rrdInventoryItem.inventoryMasterId}`;
      const formsResponse = await axios.get(url, {
        headers: { accept: 'text/plain' },
      });
      if (formsResponse.data.success) {
        const updatedRecords = formsResponse.data.result.map((record: any) => ({
          ...record,
          delete: {
            domID: 'buttodddn-id-2',
            name: 'Delete File',
            buttonType: 'diminished',
            onClick: () => handleRemoveFile(record.inventoryFileId),
            disabled: false,
            secondLine: 'some extra toggle text',
          },
        }));
        setGridRecords(updatedRecords);
      } else {
        throw new Error('Failed to fetch forms');
      }
    } catch (error) {
      console.error('Error fetching forms:', error);
    }
    // eslint-disable-next-line
  }, [rrdInventoryItem?.inventoryMasterId]);

  useEffect(() => {
    fetchForms();
  }, [rrdInventoryItem?.inventoryMasterId, refreshList, fetchForms]);

  const handleAddMore = useCallback(() => {
    setUploadItems((prevItems) => [
      ...prevItems,
      { id: prevItems.length + 1, selectedDocType: '', mailType: '', file: null, otherDocType: '', hasValidatedFile: false },
    ]);
  }, []);

  const handleRemoveItem = useCallback((id: number) => {
    setUploadItems((prevItems) => prevItems.filter((item) => item.id !== id));
  }, []);

  const handleItemChange = useCallback((id: number, field: string, value: any) => {
    setUploadItems((prevItems) =>
      prevItems.map((item) =>
        item.id === id
          ? {
            ...item,
            [field]: value,
            mailType:
              field === 'selectedDocType'
                ? value === 'Medical Record'
                  ? 'CERT'
                  : 'STD'
                : field === 'mailType'
                  ? value
                  : item.mailType,
          }
          : item
      )
    );
  }, []);

  // Handles file selection: updates state and immediately validates the chosen PDF.

  const handleFileChange = useCallback(async (id: number, file: File | null) => {

    // ✅ mark this item as validating
    setUploadItems(prev =>
      prev.map(item =>
        item.id === id ? { ...item, file, fileError: undefined, hasValidatedFile: false, isValidating: true } : item
      )
    );

    if (!file) {
      setUploadItems(prev =>
        prev.map(item =>
          item.id === id ? { ...item, hasValidatedFile: false, isValidating: false } : item
        )
      );
      return;
    }

    const result = await validatePDFFile(file);

    setUploadItems(prev =>
      prev.map(item =>
        item.id === id
          ? {
            ...item,
            fileError: result.isValid ? undefined : result.error,
            hasValidatedFile: result.isValid,
            isValidating: false   // ✅ done
          }
          : item
      )
    );
  }, []);


  const handleUpload = useCallback(async () => {
    for (const item of uploadItems) {
      const documentType = item.selectedDocType === 'Other' ? item.otherDocType : item.selectedDocType;
      if (!documentType) {
        alert(`Please select or enter a document type for item ${item.id}!`);
        return;
      }
      if (!item.mailType) {
        alert(`Please select a mail type for item ${item.id}!`);
        return;
      }
      if (!item.file) {
        alert(`Please select a file for item ${item.id}!`);
        return;
      }
    }

    if (uploadItems.some(item => item.selectedDocType === 'Package')) {
      const userConfirmed = window.confirm(
        'Warning: The "Package" document type should only be selected for request type "Payer" and must include a cover page. Do you want to continue?'
      );
      if (!userConfirmed) return;
    }

    const selectedHierarchies = uploadItems.map(item => {
      const docType = item.selectedDocType === 'Other' ? item.otherDocType : item.selectedDocType;
      return docType && typeof docType === 'string' ? docTypeHierarchy[docType] || 1 : 1;
    });
    const maxHierarchy = Math.max(...selectedHierarchies);

    if (maxHierarchy === 3 && uploadItems.length < maxHierarchy) {
      message.error(`At least ${maxHierarchy} document(s) are required based on the selected document types.`);
      return;
    }

    const hasHierarchy2 = uploadItems.some(item =>
      typeof item.selectedDocType === 'string' && docTypeHierarchy[item.selectedDocType] === 2
    );
    const hasHierarchy1 = uploadItems.some(item =>
      typeof item.selectedDocType === 'string' && docTypeHierarchy[item.selectedDocType] === 1
    );

    if (hasHierarchy2 && !hasHierarchy1) {
      const errorMessage = rrdInventoryItem?.requestType === 'Patient'
        ? 'For Patient Request Type: This document requires an additional document type of Patient Letter.'
        : 'For Payer Request Type: This document requires an additional document type of Payer Letter or Cover Letter.';
      message.error(errorMessage);
      return;
    }

    const invalidFiles = uploadItems.filter((item) => {
      if (!item.file) return true;
      const fileExtension = item.file?.name.split('.').pop()?.toLowerCase();
      return fileExtension !== 'pdf';
    });
    if (invalidFiles.length > 0) {
      message.error('All uploaded files must be in .pdf format. Please remove invalid files and try again.');
      return;
    }

    const unvalidatedFiles = uploadItems.filter((item) => item.file && !item.hasValidatedFile);
    if (unvalidatedFiles.length > 0) {
      unvalidatedFiles.forEach((item) => {
        const docLabel = item.selectedDocType === 'Other' ? item.otherDocType : item.selectedDocType;
        message.error(`"${item.file?.name}" (${docLabel || 'unspecified'}): File validation is not complete. Please reselect the file and try again.`);
      });
      return;
    }

    setRefreshList(false);
    setUploading(true);

    try {
      const baseurl = process.env.REACT_APP_RRD_OPTIMIZATION_BASE_URL as string;
      const payload = {
        inventoryMasterId: rrdInventoryItem?.inventoryMasterId,
        transactionStatus: 2,
        componentName: "Nova UI",
        componentStatus: "CS",
        remarks: "File uploading complete.",
        modifiedBy: userid,
        modifiedOn: "9999-12-12 00:00:00.000"
      };

      for (const item of uploadItems) {
        const doctypeabbreviation = getPackageAbbreviation(item.selectedDocType || '', rrdInventoryItem?.requestType);
        const mtype = item.mailType;
        const fileNamePrefix = `${doctypeabbreviation}_${mtype}_${facilityCode}_${rrdInventoryItem?.accountNumber}`;
        let doctypeseqnumber = 0;
        const documentType = item.selectedDocType === 'Other' ? item.otherDocType : item.selectedDocType;

        const seqresponse = await axios.get(`${baseurl}/Inventory/SequenceNumber/${fileNamePrefix}`, {
          headers: { 'Content-Type': 'application/json' },
        });
        if (seqresponse.data.success) {
          doctypeseqnumber = parseInt(seqresponse.data.result ? seqresponse.data.result : '1', 10);
        }
        const formattedSequenceNumber = doctypeseqnumber < 10 ? `0${doctypeseqnumber}` : doctypeseqnumber;
        const fname = `${fileNamePrefix}_${formattedSequenceNumber}`;

        const formData = new FormData();
        formData.append('inventoryFileId', '0');
        formData.append('transactionId', rrdInventoryItem?.transactionId?.toString() || '');
        formData.append('documentType', documentType || '');
        formData.append('actualFileName', item.file?.name! || '');
        formData.append('mailType', mtype);
        formData.append('packageName', '');
        formData.append('fileName', fname);
        formData.append('filePath', '');
        formData.append('region', rrdInventoryItem?.region);
        formData.append('createdBy', userid);
        formData.append('file', item.file ? item.file : new Blob());

        const url = `${baseurl}/InventoryFiles/Add`;
        const response = await axios.post(url, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        if (response.data.success) {
          message.success(`File uploaded successfully for ${item.selectedDocType === 'Other' ? item.otherDocType : item.selectedDocType}!`);
        }
      }
      try {
        const response = await axios.put(`${baseurl}/Inventory/UpdateStatus`, JSON.stringify(payload), {
          headers: { 'Content-Type': 'application/json' },
        });
        if (response.data.success) {
          refreshData();
        }
      } catch (error: any) {
        message.error('Error updating inventory status:- ' + error.response.data.message);
      }
    } catch (error: any) {
      console.error('Error uploading files:', error);
      message.error('Failed to upload files.');
    } finally {
      setRefreshList(true);
      setUploading(false);
      setUploadItems([{ id: 1, selectedDocType: '', mailType: '', file: null, otherDocType: '', hasValidatedFile: false }]);
    }
    // eslint-disable-next-line
  }, [uploadItems, facilityCode, rrdInventoryItem, userid, refreshData]);

  const handleRemoveFile = useCallback(async (id: number) => {
    const userConfirmed = window.confirm('Are you sure you want to delete this file?');
    if (userConfirmed) {
      setRefreshList(false);
      try {
        const baseurl = process.env.REACT_APP_RRD_OPTIMIZATION_BASE_URL as string;
        const url = `${baseurl}/InventoryFiles/Delete/${id}/${userid}`;
        const response = await axios.delete(url, {
          headers: { 'accept': 'text/plain' },
        });
        if (response.data.success) {
          setRefreshList(true);
          message.success('File deleted successfully!');
        }
      } catch (error: any) {
        message.error('error deleting file.');
        console.log('error deleting file:- ', error);
      }
    }
  }, [userid]);

  return (
    <div>

      <Modal
        dataTestId="test-modalHeader"
        isOpen={isOpen}
        onClickOut={() => setIsOpen(false)}
        size="large"
        domID="test-modal"
        closeModalOnClickOut={true}
      >
        <ModalHeader
          title={gridRecords.length === 0 ? "File Upload" : "File(s) Uploaded"}
          dataTestId="test-header"
          description={gridRecords.length === 0 ? "Fill in details below and click to upload file" : "The uploaded files are listed below."}
          onClose={() => setIsOpen(false)}
        />
        <ModalBody>
          <Container style={{ padding: '10px', maxWidth: '90%', margin: '0 auto' }}>
            {gridRecords.length === 0 && (
              <div>
                <div>
                  {uploadItems.map((item) => (
                    <div
                      key={item.id}
                      style={{
                        marginBottom: '10px',
                        border: '1px solid #ccc',
                        padding: '8px',
                        borderRadius: '5px',
                        backgroundColor: '#f9f9f9',
                      }}
                    >
                      <Row>
                        <div style={{ width: '35%', marginLeft: '1rem' }}>
                          <label htmlFor={`doctype-${item.id}`} style={{ fontWeight: 'bold', fontSize: '14px' }} className="uicl-label">
                            Type of Document/Package <span style={{ color: 'red' }}>*</span>
                          </label>
                          <SelectDropdown
                            ariaLabel="ARIA label"
                            dataTestId={`doctype-selectDropdown-${item.id}`}
                            domID={`doctype-${item.id}`}
                            placeholderText="Select..."
                            isClearable={false}
                            zIndex={99}
                            options={docTypeOptions}
                            initialValue={{ label: item.selectedDocType, value: item.selectedDocType }}
                            onChange={(e) => handleItemChange(item.id, 'selectedDocType', e?.value ?? '')}
                          />
                          {item.selectedDocType === 'Other' && (
                            <div style={{ marginTop: '10px' }}>
                              <label htmlFor={`otherDocType-${item.id}`} style={{ fontWeight: 'bold', fontSize: '14px' }} className="uicl-label">
                                Enter Document Name <span style={{ color: 'red' }}>*</span>
                              </label>
                              <Input
                                domID={`otherDocType-${item.id}`}
                                aria-label="Enter Document Name"
                                placeholder="Enter document name..."
                                value={item.otherDocType || ''}
                                onChange={(e) => handleItemChange(item.id, 'otherDocType', e.target.value)}
                                style={{ width: '100%' }}
                              />
                            </div>
                          )}
                        </div>
                        <div style={{ width: '20%', marginLeft: '10px' }}>
                          <label htmlFor={`mailtype-${item.id}`} style={{ fontWeight: 'bold', fontSize: '14px' }} className="uicl-label">
                            Mail Type <span style={{ color: 'red' }}>*</span>
                          </label>
                          <SelectDropdown
                            ariaLabel="ARIA label"
                            dataTestId={`mailtype-selectDropdown-${item.id}`}
                            domID={`mailtype-${item.id}`}
                            placeholderText="Select..."
                            isClearable={false}
                            zIndex={99}
                            disabled={rrdInventoryItem?.requestType == 'Patient' && item.selectedDocType === 'Patient Letter'}
                            options={mailTypeOptions}
                            initialValue={{ label: item.mailType, value: item.mailType }}
                            onChange={(e) => handleItemChange(item.id, 'mailType', e?.value ?? '')}
                          />
                        </div>
                        <div style={{ marginTop: '25px', marginLeft: '10px' }}>
                          <input
                            id={`file-upload-${item.id}`}
                            type="file"
                            accept=".pdf"
                            onChange={(e) => handleFileChange(item.id, e.target.files?.[0] || null)}
                            style={{ display: 'block', marginTop: '5px', fontSize: '14px' }}
                          />
                          {item.fileError && (
                            <div style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>
                              &#9888; {item.fileError}
                            </div>
                          )}
                        </div>
                        {item.id !== 1 && (
                          <div style={{ marginTop: '15px', marginLeft: '10px' }}>
                            <Button buttonType="diminished" onClick={() => handleRemoveItem(item.id)}>
                              Remove
                            </Button>
                          </div>
                        )}
                      </Row>
                    </div>
                  ))}
                </div>
                <div>
                  <Row style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Button
                      onClick={handleUpload}
                      loading={uploading || isAnyFileValidating}
                      disabled={
                        uploading ||
                        isAnyFileValidating ||        // ✅ new check
                        uploadItems.some(item => !!item.fileError)
                      }
                    >
                      {uploading ? 'Uploading...' : isAnyFileValidating ? 'Validating...' : 'Upload'}
                    </Button>

                    {uploadStatusMessage && (
                      <div style={{ color: '#555', fontSize: '13px', fontWeight: 600 }}>
                        {uploadStatusMessage}
                      </div>
                    )}

                    <Button
                      buttonType="emphasized"
                      onClick={handleAddMore}
                      disabled={uploading || isAnyFileValidating}
                      style={{ marginRight: '1rem' }}
                    >
                      + Add More
                    </Button>
                  </Row>
                </div>
              </div>
            )}
            <div style={{ marginTop: '10px' }}>
              <div style={{ width: '100%' }}>
                <Grid
                  columns={columns}
                  dataTestId="test-gridDefault"
                  domID="test-id"
                  records={gridRecords}
                  supportSelection={false}
                  selectionKey="id"
                />
              </div>
            </div>
          </Container>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup>
            <Button buttonType="diminished" name="Close" onClick={() => setIsOpen(false)} />
          </ButtonGroup>
        </ModalFooter>
      </Modal>
    </div>
  );
});

export default RRDDocumentUpload;